import { ChangeContext, Options } from '@angular-slider/ngx-slider';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import clone from 'clone-deep';

@Component({
    selector: 'ca-slider',
    templateUrl: './slider.component.html',
    styleUrls: ['./slider.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: SliderComponent,
            multi: true,
        },
    ],
})
export class SliderComponent implements ControlValueAccessor, OnInit {
    // used to set ngx-slider [value], which sets the single value or minValue
    get value(): number | undefined {
        if (this._value == null) {
            return this.minValue;
        }

        return Array.isArray(this._value) ? this._value[0] : this._value;
    }
    @Input() set value(v: number | undefined) {
        if (v !== this._value) {
            // check if the value is within min and max
            if (v != null && this.minValue != null && v < this.minValue) {
                v = this.minValue;
            }
            if (v != null && this.maxValue != null && v > this.maxValue) {
                v = this.maxValue;
            }
            this._value = v;

            // we need to let the change detection know.
            this.changeDetectorRef.markForCheck();
        }
    }
    @Input() set minValue(value: number | undefined) {
        if (value != null) {
            this.options.floor = value;

            // Clone object after change, because we need to change the reference
            this.options = clone(this.options);
        }
    }
    @Input() set maxValue(value: number | undefined) {
        if (value != null) {
            this.options.ceil = value;

            // Clone object after change, because we need to change the reference
            this.options = clone(this.options);
        }
    }
    @Input() set step(value: number | null) {
        if (value != null) {
            this.options.step = value;

            // Clone object after change, because we need to change the reference
            this.options = clone(this.options);
        }
    }
    @Input() set isDisabled(value: boolean) {
        if (this.options.disabled !== value) {
            // Clone object after change, because we need to change the reference
            this.options = Object.assign({}, this.options, { disabled: value });
        }
    }

    get isDisabled(): boolean {
        return this.options.disabled || false;
    }

    @Input() type: 'single' | 'dual' = 'single';
    @Input() highSliderValue: number = 0;
    @Input() manualRefresh?: EventEmitter<void>;

    /**
     * Emits an event when the User changed the slider value
     * for a 'single' slider the value is of type 'number'
     * for a 'dual' slider the value is of type 'number[]' - minValue at position 0, maxValue position 1
     */
    @Output() onUserChangeEnd: EventEmitter<number | number[]> = new EventEmitter<number | number[]>();

    /**
     * Emits an event when the slider value changes, no matter of from the user or programmatically
     * for a 'single' slider the value is of type 'number'
     * for a 'dual' slider the value is of type 'number[]' - minValue at position 0, maxValue position 1
     */
    @Output() onValueChange: EventEmitter<number | number[]> = new EventEmitter<number | number[]>();

    get minValue(): number | undefined {
        return this.options.floor;
    }

    get maxValue(): number | undefined {
        return this.options.ceil;
    }

    options: Options = {
        floor: 0,
        ceil: 100,
        step: 1,
        disabled: false,
    };

    /**
     * variable type is depending on slider type 'single' or 'dual'
     */
    private _value: number | number[] | undefined = undefined;

    constructor(private changeDetectorRef: ChangeDetectorRef) {}

    ngOnInit(): void {
        if (!this.manualRefresh) {
            this.manualRefresh = new EventEmitter();
        }
    }

    onChange(value: number | number[]): void {
        this._value = value;
    }

    onTouched(): void {
        // do nothing
    }

    valueChange(event: number): void {
        this.onValueChange.emit(event);
    }

    rangeSliderValueChange(val: number): void {
        if (!Number.isInteger(val)) {
            return;
        }
        const numbers: number[] = [val, this.highSliderValue];
        this.onValueChange.emit(numbers);
    }

    rangeSliderHighValueChange(val: number): void {
        if (!Number.isInteger(val)) {
            return;
        }
        const numberVal: number = this.value as number;
        const numbers: number[] = [numberVal, val];
        this.onValueChange.emit(numbers);
    }

    /**
     * for type 'single', emit single value of type number
     * for type 'dual', emit both values as a number[]
     */
    onSliderChange(valueObj: ChangeContext): void {
        let changedValue: number | number[] = valueObj.value;

        if (this.type === 'dual') {
            if (valueObj.highValue != null) {
                this.highSliderValue = valueObj.highValue;
            } else if (this.maxValue != null) {
                this.highSliderValue = this.maxValue;
            } else {
                // If none applies, we need to set some artificial high value
                this.highSliderValue = 100;
            }
            changedValue = [valueObj.value, this.highSliderValue];
        }
        this.onUserChangeEnd.emit(changedValue);
        this.onChange(changedValue);
    }

    checkSliderContent(e: KeyboardEvent): boolean {
        return (e.keyCode >= 48 && e.keyCode <= 57) || e.keyCode === 46 || e.keyCode === 45;
    }

    writeValue(sliderValue: number | number[]): void {
        this._value = sliderValue;
    }

    registerOnChange(fn: (value: number | number[]) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    updateSlider(event: MouseEvent): void {
        const inputEl = event.target as HTMLInputElement;
        const val = parseFloat(inputEl.value);
        this.value = val;
        this.onChange(val);
    }

    onFocusOut(event: MouseEvent): void {
        const inputEl = event.target as HTMLInputElement;
        const val = parseFloat(inputEl.value);
        if (val !== this.value) {
            this.updateSlider(event);
        }
    }

    disableEventBubbling(event: Event): void {
        event.stopPropagation();
    }
}
